"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.RecordManualPaymentResponse = exports.RecordManualPaymentBody = exports.PayoutAttemptsQuery = exports.PayoutAttemptTransport = exports.PlaidNetworkSchema = exports.PlaidNetworks = exports.TransferNetworkLabels = exports.TransferNetworkSchema = exports.TransferNetworks = exports.AttemptStatus = void 0;
const typebox_1 = require("@sinclair/typebox");
const common_1 = require("../common");
const vendor_payment_profile_transport_1 = require("./vendor-payment-profile-transport");
/**
 * Payout Attempt Transport Types
 *
 * Represents individual attempts to send payment to a vendor.
 * Each attempt tracks the complete lifecycle of a payment initiation,
 * including provider interactions and final status.
 */
// Payment method - use common type for full set of payment methods
const PaymentMethod = common_1.PaymentMethod;
// Payment provider enum (includes MANUAL for non-automated payments)
const PaymentProvider = typebox_1.Type.Union([typebox_1.Type.Literal('PLAID'), typebox_1.Type.Literal('SVB'), typebox_1.Type.Literal('MANUAL')]);
// Attempt status enum
exports.AttemptStatus = typebox_1.Type.Union([
    typebox_1.Type.Literal('INITIATED'),
    typebox_1.Type.Literal('PENDING'),
    typebox_1.Type.Literal('AUTHORIZED'),
    typebox_1.Type.Literal('COMPLETED'),
    typebox_1.Type.Literal('FAILED'),
    typebox_1.Type.Literal('CANCELLED'),
    typebox_1.Type.Literal('RETURNED'),
]);
// Transfer network types (the actual network used for fund transfers)
exports.TransferNetworks = ['ach', 'same-day-ach', 'rtp', 'wire'];
exports.TransferNetworkSchema = typebox_1.Type.Union(exports.TransferNetworks.map((n) => typebox_1.Type.Literal(n)));
exports.TransferNetworkLabels = {
    ach: 'ACH',
    'same-day-ach': 'Same-Day ACH',
    rtp: 'RTP (Instant)',
    wire: 'Wire',
};
/** @deprecated Use TransferNetworks */
exports.PlaidNetworks = exports.TransferNetworks;
/** @deprecated Use TransferNetworkSchema */
exports.PlaidNetworkSchema = exports.TransferNetworkSchema;
// Main transport schema for payout attempts
exports.PayoutAttemptTransport = typebox_1.Type.Object({
    id: typebox_1.Type.String({ format: 'uuid' }),
    payoutID: typebox_1.Type.String({ format: 'uuid' }),
    attemptTimestamp: typebox_1.Type.String(), // ISO 8601 timestamp
    paymentMethod: PaymentMethod,
    provider: PaymentProvider,
    providerTransferID: typebox_1.Type.Optional(typebox_1.Type.String()),
    providerAuthorizationID: typebox_1.Type.Optional(typebox_1.Type.String()),
    // ACH destination snapshot
    plaidAccountID: typebox_1.Type.Optional(typebox_1.Type.String()),
    accountMask: typebox_1.Type.Optional(typebox_1.Type.String()),
    accountName: typebox_1.Type.Optional(typebox_1.Type.String()),
    accountType: typebox_1.Type.Optional(typebox_1.Type.String()),
    // VCC destination snapshot
    svbCardId: typebox_1.Type.Optional(typebox_1.Type.String({ format: 'uuid' })),
    cardLastFour: typebox_1.Type.Optional(typebox_1.Type.String()),
    // Additional audit fields
    vendorBusinessName: typebox_1.Type.Optional(typebox_1.Type.String()),
    achSpeed: typebox_1.Type.Optional(vendor_payment_profile_transport_1.ACHSpeed),
    transferNetwork: typebox_1.Type.Optional(exports.TransferNetworkSchema),
    /** When funds are expected to arrive, or actual payment date for manual payments. ISO 8601 timestamp. */
    expectedArrivalDate: typebox_1.Type.Optional(typebox_1.Type.String()),
    amount: typebox_1.Type.Integer({ minimum: 0 }),
    status: exports.AttemptStatus,
    failureCode: typebox_1.Type.Optional(typebox_1.Type.String()),
    failureMessage: typebox_1.Type.Optional(typebox_1.Type.String()),
    completedAt: typebox_1.Type.Optional(typebox_1.Type.String()), // ISO 8601 timestamp
    failedAt: typebox_1.Type.Optional(typebox_1.Type.String()), // ISO 8601 timestamp
    durationMs: typebox_1.Type.Union([typebox_1.Type.Number(), typebox_1.Type.Null()]),
    // Computed flags
    isInProgress: typebox_1.Type.Boolean(),
    isCompleted: typebox_1.Type.Boolean(),
    isFailed: typebox_1.Type.Boolean(),
    // Entity metadata
    metadata: typebox_1.Type.Optional(typebox_1.Type.Any()),
});
// Query parameters for listing payout attempts
exports.PayoutAttemptsQuery = typebox_1.Type.Object({
    limit: typebox_1.Type.Optional(typebox_1.Type.Integer({ minimum: 1, maximum: 100 })),
    offset: typebox_1.Type.Optional(typebox_1.Type.Integer({ minimum: 0 })),
    payoutID: typebox_1.Type.Optional(typebox_1.Type.String({ format: 'uuid' })),
    paymentMethod: typebox_1.Type.Optional(PaymentMethod),
    provider: typebox_1.Type.Optional(PaymentProvider),
    status: typebox_1.Type.Optional(exports.AttemptStatus),
    minAmount: typebox_1.Type.Optional(typebox_1.Type.Integer({ minimum: 0 })),
    maxAmount: typebox_1.Type.Optional(typebox_1.Type.Integer({ minimum: 0 })),
    startDate: typebox_1.Type.Optional(typebox_1.Type.String()), // ISO 8601 timestamp
    endDate: typebox_1.Type.Optional(typebox_1.Type.String()), // ISO 8601 timestamp
    sortBy: typebox_1.Type.Optional(typebox_1.Type.Union([typebox_1.Type.Literal('attemptTimestamp'), typebox_1.Type.Literal('amount'), typebox_1.Type.Literal('status')])),
    sortOrder: typebox_1.Type.Optional(typebox_1.Type.Union([typebox_1.Type.Literal('asc'), typebox_1.Type.Literal('desc')])),
});
/**
 * Request body for POST /invoice/payout-attempts/:id/record-payment
 *
 * Field mapping:
 * - paymentIdentifier → PayoutAttempt.providerTransferID + InvoicePayment.paymentIdentifier
 * - paymentDate → PayoutAttempt.expectedArrivalDate + InvoicePayment.date/paymentSentDate
 * - notes → InvoicePayment.notes (not stored on attempt)
 */
exports.RecordManualPaymentBody = typebox_1.Type.Object({
    /** Check #, wire ref, ACH confirmation, etc. Stored as providerTransferID on attempt. */
    paymentIdentifier: typebox_1.Type.Optional(typebox_1.Type.String()),
    /** ISO 8601 date when payment was made. Defaults to now. Stored as expectedArrivalDate on attempt. */
    paymentDate: typebox_1.Type.Optional(typebox_1.Type.String()),
    /** Notes about the payment. Only stored on InvoicePayment records, not on the attempt. */
    notes: typebox_1.Type.Optional(typebox_1.Type.String()),
});
// Response for POST /invoice/payout-attempts/:id/record-payment
exports.RecordManualPaymentResponse = typebox_1.Type.Object({
    success: typebox_1.Type.Boolean(),
    attemptID: typebox_1.Type.String({ format: 'uuid' }),
    payoutID: typebox_1.Type.String({ format: 'uuid' }),
    paymentsCreated: typebox_1.Type.Integer({ minimum: 0 }),
    totalAmount: typebox_1.Type.Integer({ minimum: 0 }),
});
//# sourceMappingURL=payout-attempt-transport.js.map